import { Drawer as MantineDrawer, DrawerProps, useMantineTheme, Box, useMantineColorScheme, MantineSize } from '@mantine/core';
import { forwardRef, CSSProperties } from 'react';
import { useMediaQuery, useReducedMotion } from '@mantine/hooks';
import { usePreferencesStore } from '../../../stores';

export type GradientVariant = 'primary' | 'secondary' | 'tertiary' | 'success' | 'info' | 'warning' | 'danger';
export type DrawerVariant = 'default' | 'gradient' | 'elevated' | 'minimal' | 'bordered' | 'glass';
export type DrawerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
export type DrawerPosition = 'left' | 'right' | 'top' | 'bottom';
export type BackdropVariant = 'default' | 'blur' | 'transparent' | 'dark' | 'light' | 'none';

export interface ThemedDrawerProps extends Omit<DrawerProps, 'size' | 'position' | 'closeOnClickOutside' | 'closeOnEscape'> {
  gradientHeader?: boolean;
  gradientVariant?: GradientVariant;
  variant?: DrawerVariant;
  size?: DrawerSize;
  position?: DrawerPosition;
  backdropVariant?: BackdropVariant;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  backgroundImage?: string;
  backgroundColor?: string;
  thumbnail?: React.ReactNode;
  borderSides?: ('top' | 'bottom' | 'left' | 'right')[];
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  padding?: number | string;
  responsiveBreakpoint?: number | string | MantineSize;
  disableMotion?: boolean;
}

const Drawer = forwardRef<HTMLDivElement, ThemedDrawerProps>(
  (
    {
      gradientHeader = false,
      gradientVariant = 'primary',
      variant = 'default',
      size = 'md',
      position = 'left',
      backdropVariant = 'default',
      closeOnClickOutside = true,
      closeOnEscape,
      backgroundImage,
      backgroundColor,
      thumbnail,
      borderSides,
      borderColor,
      borderWidth = 2,
      borderRadius,
      padding,
      responsiveBreakpoint = 'sm',
      disableMotion = false,
      styles,
      children,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const gradient = theme.other.gradients[gradientVariant];
    const isDark = colorScheme === 'dark';
    const resolvedCloseOnEscape = closeOnEscape ?? closeOnClickOutside;
    const reducedMotion = useReducedMotion() || disableMotion;
    const highContrast = usePreferencesStore((state) => state.highContrast);
    const breakpointValue =
      typeof responsiveBreakpoint === 'number'
        ? responsiveBreakpoint
        : theme.breakpoints?.[responsiveBreakpoint as MantineSize];
    const isSmallScreen = breakpointValue ? useMediaQuery(`(max-width: ${breakpointValue}px)`) : false;

    // Size mapping
    const sizeMap: Record<DrawerSize, string | number> = {
      xs: '300px',
      sm: '400px',
      md: '500px',
      lg: '700px',
      xl: '900px',
      full: '100vw',
    };
    const computedSize = isSmallScreen ? '100vw' : sizeMap[size];

    // Backdrop styles
    const getBackdropStyles = (): CSSProperties => {
      switch (backdropVariant) {
        case 'blur':
          return {
            backdropFilter: 'blur(8px)',
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.4)',
          };
        case 'transparent':
          return {
            backgroundColor: 'transparent',
          };
        case 'dark':
          return {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          };
        case 'light':
          return {
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          };
        case 'none':
          return {
            display: 'none',
          };
        case 'default':
        default:
          return {
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.55)',
          };
      }
    };

    // Variant styles
    const getVariantStyles = (): CSSProperties => {
      const defaultBg = backgroundColor || (isDark ? theme.colors.dark[7] : theme.white);
      const defaultColor = isDark ? theme.colors.gray[2] : theme.colors.dark[7];
      const baseStyle: CSSProperties = {
        backgroundColor: defaultBg,
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: defaultColor,
      };

      switch (variant) {
        case 'gradient':
          return {
            ...baseStyle,
            backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
            color: 'white',
          };
        case 'elevated':
          return {
            ...baseStyle,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          };
        case 'minimal':
          return {
            ...baseStyle,
            boxShadow: 'none',
            border: 'none',
          };
        case 'bordered':
          return {
            ...baseStyle,
            border: `${borderWidth}px solid ${borderColor || (isDark ? theme.colors.dark[4] : theme.colors.gray[3])}`,
          };
        case 'glass':
          return {
            ...baseStyle,
            backgroundColor: isDark ? 'rgba(26, 27, 30, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)'}`,
          };
        case 'default':
        default:
          return baseStyle;
      }
    };

    // Border sides styles
    const getBorderSidesStyles = (): CSSProperties => {
      if (!borderSides || borderSides.length === 0) return {};

      const color = borderColor || (isDark ? theme.colors.dark[4] : theme.colors.gray[3]);
      const width = borderWidth;

      return {
        borderTop: borderSides.includes('top') ? `${width}px solid ${color}` : undefined,
        borderBottom: borderSides.includes('bottom') ? `${width}px solid ${color}` : undefined,
        borderLeft: borderSides.includes('left') ? `${width}px solid ${color}` : undefined,
        borderRight: borderSides.includes('right') ? `${width}px solid ${color}` : undefined,
      };
    };

    // Combine all drawer content styles
    const drawerContentStyles: CSSProperties = {
      ...getVariantStyles(),
      ...getBorderSidesStyles(),
      borderRadius: borderRadius !== undefined ? `${borderRadius}px` : undefined,
      padding: padding !== undefined ? (typeof padding === 'number' ? `${padding}px` : padding) : undefined,
      border: highContrast ? `1px solid ${isDark ? theme.colors.gray[5] : theme.colors.dark[4]}` : undefined,
    };

    // Handle gradient header styling
    const headerStyles = gradientHeader
      ? {
          backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
          color: 'white',
          ...(styles as any)?.header,
        }
      : {
          backgroundColor: 'transparent',
          borderBottom: 'none',
          ...(styles as any)?.header,
        };

    const titleStyles = gradientHeader
      ? {
          color: 'white',
          fontWeight: 600,
          ...(styles as any)?.title,
        }
      : {
          color: isDark ? theme.white : theme.colors.dark[9],
          fontWeight: 600,
          ...(styles as any)?.title,
        };

    const closeStyles = gradientHeader
      ? {
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          ...(styles as any)?.close,
        }
      : {
          color: isDark ? theme.colors.gray[5] : theme.colors.gray[7],
          '&:hover': {
            backgroundColor: isDark ? theme.colors.dark[5] : theme.colors.gray[0],
          },
          ...(styles as any)?.close,
        };

    return (
      <MantineDrawer
        ref={ref}
        size={computedSize as any}
        closeOnClickOutside={closeOnClickOutside}
        closeOnEscape={resolvedCloseOnEscape}
        position={position}
        transitionProps={{ duration: reducedMotion ? 0 : 200 }}
        withOverlay={!isSmallScreen || rest.withOverlay !== false}
        styles={{
          ...styles,
          overlay: {
            ...getBackdropStyles(),
            ...(styles as any)?.overlay,
          },
          content: {
            ...drawerContentStyles,
            ...(styles as any)?.content,
          },
          header: headerStyles,
          title: titleStyles,
          close: closeStyles,
        }}
        {...rest}
      >
        {thumbnail && (
          <Box
            style={{
              position: 'relative',
              width: '100%',
              marginBottom: theme.spacing.md,
              marginTop: `-${theme.spacing.md}px`,
              marginLeft: `-${theme.spacing.md}px`,
              marginRight: `-${theme.spacing.md}px`,
              borderTopLeftRadius: borderRadius !== undefined ? `${borderRadius}px` : theme.radius.md,
              borderTopRightRadius: borderRadius !== undefined ? `${borderRadius}px` : theme.radius.md,
              overflow: 'hidden',
            }}
          >
            {thumbnail}
          </Box>
        )}
        {children}
      </MantineDrawer>
    );
  }
);

Drawer.displayName = 'Drawer';

export default Drawer;
